import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { getReconciliationEngine } from 'services'
import { svgLoading } from 'stories/assets'
import { thousandSeperator } from 'utils'

export interface IReconciliationRow {
  control: string
  sourceModule: string
  moduleAmount: number
  glAccountNo: string
  glBalance: number
  difference: number
  status: 'OK' | 'INVESTIGATE'
}

const formatAmount = (value?: number) => {
  if (!value && value !== 0) return '-'
  return `$${thousandSeperator(value)}`
}

export const ReconciliationEngine = () => {
  const [isLoading, setLoading] = useState(false)
  const [rows, setRows] = useState<IReconciliationRow[]>([])
  const [allTie, setAllTie] = useState(false)

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getReconciliationEngine()
      setRows(result.results || [])
      setAllTie(Boolean(result.summary?.allTie))
    } catch (error) {
      console.error(error)
      setRows([])
    }
    setLoading(false)
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Reconciliation Engine
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Each subledger / module total is cross-footed against its GL control account. Every Difference must be $0.
        This is where &quot;everything connects&quot; is proven.
      </p>

      <div className="relative overflow-x-auto">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-700 min-w-max">
          <thead className="text-xs uppercase bg-gray-100">
            <tr>
              {['Control', 'Source Module', 'Module Amount', 'GL Acct', 'GL Balance', 'Difference', 'Status'].map(
                (h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap">
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                  No reconciliation data yet.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={`${row.control}-${row.glAccountNo}`} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{row.control}</td>
                <td className="px-3 py-2 whitespace-nowrap">{row.sourceModule}</td>
                <td className="px-3 py-2 text-right">{formatAmount(row.moduleAmount)}</td>
                <td className="px-3 py-2">{row.glAccountNo}</td>
                <td className="px-3 py-2 text-right">{formatAmount(row.glBalance)}</td>
                <td className="px-3 py-2 text-right">{formatAmount(row.difference)}</td>
                <td className="px-3 py-2">
                  <span className={row.status === 'OK' ? 'text-green-700 font-semibold' : 'text-red-600 font-semibold'}>
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="bg-green-50 font-semibold border-t-2">
                <td colSpan={6} className="px-3 py-2">
                  ALL MODULES RECONCILED?
                </td>
                <td className="px-3 py-2">
                  <span className={allTie ? 'text-green-700' : 'text-red-600'}>{allTie ? 'ALL TIE' : 'INVESTIGATE'}</span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
